import { lazy, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import Header from '~/app/components/Header'
import PostPaymentProgressBar from '~/shared/components/PostPaymentProgressBar'
import { TEST_ENV_QUERY_PARAM } from '~/shared/constants/commonConstants'
import { ThemeProvider } from '~/shared/contexts/ThemeContext'
import { getEnvironment } from '~/shared/helpers/getEnvironment'
import { useBulkSelector } from '~/shared/hooks/useBulkSelector'
import { selectCohortID, selectLanguage } from '~/shared/store/common/selects'
import {
  selectUserCountryCode,
  selectUserUUID,
} from '~/shared/store/user/selects'

import { useAmplitudeInitialization } from '../../hooks/useAmplitudeInitialization'
import { useApplovinPixelInitialization } from '../../hooks/useApplovinPixelInitialization'
import { useDirectStripePaymentFeature } from '../../hooks/useDirectStripePaymentFeature'
import { useDynamicDiscount } from '../../hooks/useDynamicDiscount'
import { useGiaFlowTrackerInitialization } from '../../hooks/useGiaFlowTrackerInitialization'
import { useScrollToTop } from '../../hooks/useScrollToTop'
import { useStartSession } from '../../hooks/useStartSession'
import { useTiktokPixelInitialization } from '../../hooks/useTiktokPixelInitialization'
import { useUtmTags } from '../../hooks/useUtmTags'
import { growthBookInstance } from '../../lib/growthBook'
import { setI18nInterceptorCallback, useI18nInterceptor } from '../../lib/i18n'
import { RouteList } from '../../router/RouteList'
import { GlobalStyles } from './AppLayout.styles'

const LanguageSelector = lazy(() =>
  import('../../components/LanguageSelector').then((m) => ({
    default: m.LanguageSelector,
  })),
)
const InContextPopup = lazy(() =>
  import('../../components/InContextPopup').then((m) => ({
    default: m.InContextPopup,
  })),
)
const TranslationKeysModal = lazy(() =>
  import('../../components/TranslationKeysModal').then((m) => ({
    default: m.TranslationKeysModal,
  })),
)
const GoToPaywallButton = lazy(() =>
  import('../../components/GoToPaywallButton').then((m) => ({
    default: m.GoToPaywallButton,
  })),
)

const useSelectors = () =>
  useBulkSelector({
    uuid: selectUserUUID,
    cohort: selectCohortID,
    language: selectLanguage,
    userCountryCode: selectUserCountryCode,
  })

export const AppLayout = () => {
  const { search, pathname } = useLocation()
  // const dispatch = useDispatch()

  const { userCountryCode, cohort, language } = useSelectors()
  // const { isSecondPage } = usePageInfoContext()
  const { addTranslation } = useI18nInterceptor()

  const { isStageEnvironment } = getEnvironment()

  const hasTestEnvParam = new URLSearchParams(search).has(TEST_ENV_QUERY_PARAM)
  const isLanguageSelectorShown = isStageEnvironment || hasTestEnvParam

  useUtmTags()
  useGiaFlowTrackerInitialization()
  useAmplitudeInitialization()
  useTiktokPixelInitialization()
  useApplovinPixelInitialization()
  useStartSession()
  useScrollToTop()
  useDynamicDiscount()
  useDirectStripePaymentFeature()

  useEffect(() => {
    growthBookInstance.setAttributes({
      ...growthBookInstance.getAttributes(),
      url: `${pathname}${search}`,
      country: userCountryCode,
      cohort,
      language,
    })
  }, [cohort, language, pathname, search, userCountryCode])

  // useEffect(() => {
  //   if (isSecondPage) {
  //     dispatch(sendFacebookParamsAction())
  //     dispatch(sendGoogleAdsParamsAction())
  //     window.fbq('track', 'CustomizeProduct', {}, { eventID: uuid })
  //     eventLogger.logFirstPageCompleted()
  //     googleAnalyticsLogger.logFirstPageCompleted()
  //   }
  // }, [dispatch, isSecondPage, uuid])

  useEffect(() => {
    if (isStageEnvironment) setI18nInterceptorCallback(addTranslation)
  }, [addTranslation, isStageEnvironment])

  return (
    <ThemeProvider>
      <GlobalStyles />
      <Header />
      <PostPaymentProgressBar />
      <RouteList />
      {isLanguageSelectorShown && <LanguageSelector />}

      {isStageEnvironment ? (
        <>
          <InContextPopup />
          <TranslationKeysModal />
          <GoToPaywallButton />
        </>
      ) : null}
    </ThemeProvider>
  )
}
